/**
 * Weight helpers for the recruiter's competency selection (the wizard and the assessment editor).
 *
 * The candidate-level number is NOT computed here any more: since 0099 an evaluation's capability
 * and coverage come from evaluation.ts (capabilityOf / coverageOf), over EVIDENCED competencies only.
 */
/** Weight used for an id the map knows nothing about. */
const UNLISTED_WEIGHT = 1;
/**
 * The share of the composite each competency carries, as integer percentages that sum to 100
 * (largest-remainder rounding), in the order of `ids`. For the recruiter's screen — the stored
 * weights stay exactly as typed (1-100 each); only the arithmetic normalises.
 */
export function weightPercents(ids, weights) {
    const raw = ids.map((id) => {
        const w = weights.get(id) ?? UNLISTED_WEIGHT;
        return w > 0 ? w : 0;
    });
    const total = raw.reduce((a, b) => a + b, 0);
    if (ids.length === 0)
        return [];
    if (total <= 0) {
        // Every weight excluded → the plain mean, i.e. equal shares.
        const each = Math.floor(100 / ids.length);
        const out = ids.map(() => each);
        let rest = 100 - each * ids.length;
        for (let i = 0; rest > 0; i++, rest--)
            out[i] += 1;
        return out;
    }
    const exact = raw.map((w) => (w / total) * 100);
    const floors = exact.map((x) => Math.floor(x));
    let rest = 100 - floors.reduce((a, b) => a + b, 0);
    const order = exact
        .map((x, i) => ({ i, frac: x - Math.floor(x) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    for (const { i } of order) {
        if (rest <= 0)
            break;
        floors[i] += 1;
        rest--;
    }
    return floors;
}
/**
 * Why a recruiter's weight map is unusable against a competency selection, or null when it is
 * fine. The API refuses on a message; the wizard disables Continue on the same one. Rules:
 *   - every key must be one of the selected competencies (a weight for an unassessed area means
 *     nothing and would silently vanish);
 *   - every selected competency must have a weight (a half-filled map would rank the missing ones
 *     at weight 1 against, say, 60 for the rest — never what anyone typed);
 *   - values are integers 1-100 (the schema enforces the shape; this re-checks so a caller that
 *     skipped the schema still cannot store a 0 or a fraction).
 */
export function competencyWeightsProblem(weights, selected) {
    if (weights == null)
        return null;
    const sel = new Set(selected);
    for (const [id, w] of Object.entries(weights)) {
        if (!sel.has(id))
            return `Weight given for "${id}", which is not one of the selected assessment areas`;
        if (!Number.isInteger(w) || w < 1 || w > 100)
            return `Weight for "${id}" must be a whole number from 1 to 100`;
    }
    for (const id of sel) {
        if (!(id in weights))
            return `No weight given for "${id}" — every selected assessment area needs one`;
    }
    return null;
}
/**
 * Two weight maps describe the same weighting when they agree on every key. Order-insensitive.
 * Used to decide whether a rubric snapshot can be reused and whether a PATCH actually changed
 * anything worth an event.
 */
export function sameWeights(a, b) {
    const ka = Object.keys(a ?? {}).sort();
    const kb = Object.keys(b ?? {}).sort();
    if (ka.length !== kb.length)
        return false;
    for (let i = 0; i < ka.length; i++) {
        if (ka[i] !== kb[i])
            return false;
        if ((a ?? {})[ka[i]] !== (b ?? {})[kb[i]])
            return false;
    }
    return true;
}
//# sourceMappingURL=scoring.js.map